import { inject, Injectable, signal, computed } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { AppRole } from './app-role.enum';
import { parseAccessTokenRoles } from './parse-access-token-roles';
import { environment } from '@env';
import { AccountInfo } from '@azure/msal-browser';

const ACCESS_TOKEN_KEY = 'access_token';

export interface UserProfile {
  username: string;
  name: string;
  roles: string[];
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly authService = inject(MsalService);
  private readonly _profile = signal<UserProfile | null>(null);

  readonly profile = this._profile.asReadonly();
  readonly roles = computed(() => this._profile()?.roles ?? []);
  readonly isAdmin = computed(() => this.roles().includes(AppRole.BackOfficeAdmin));

  isAuthenticated(): boolean {
    return !!this.authService.instance.getActiveAccount();
  }

  /**
   * Initialise le service après un événement MSAL (redirect ou inProgress).
   * Sélectionne le compte actif, acquiert un token et construit le profil.
   */
  async init(account?: AccountInfo): Promise<void> {
    if (account) {
      this.authService.instance.setActiveAccount(account);
    }

    await this.acquireTokenAndBuildProfile();
  }

  /**
   * Récupère les rôles utilisateur (utilisé par les guards).
   * Cascade : mémoire → localStorage → acquireTokenSilent.
   */
  async getUserRoles(): Promise<string[]> {
    if (this._profile() && this.isAuthenticated()) {
      return this.roles();
    }

    const storedToken = localStorage.getItem(ACCESS_TOKEN_KEY);
    if (storedToken && this.isAuthenticated()) {
      this.buildProfile(storedToken);
      return this.roles();
    }

    await this.acquireTokenAndBuildProfile();
    return this.roles();
  }

  clearSession(): void {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    this._profile.set(null);
  }

  private async acquireTokenAndBuildProfile(): Promise<void> {
    const account = this.authService.instance.getActiveAccount();
    if (!account) {
      this.clearSession();
      return;
    }

    try {
      const result = await this.authService.instance.acquireTokenSilent({
        scopes: [...environment.apiConfig.scopes],
        account,
      });
      localStorage.setItem(ACCESS_TOKEN_KEY, result.accessToken);
      this.buildProfile(result.accessToken);
    } catch {
      this.clearSession();
    }
  }

  private buildProfile(accessToken: string): void {
    const account = this.authService.instance.getActiveAccount();
    if (!account) return;

    this._profile.set({
      username: account.username,
      name: account.name ?? account.username,
      roles: parseAccessTokenRoles(accessToken),
    });
  }
}
